import { datadogRum } from '@datadog/browser-rum';
import { Footer } from '@shared/components/footer';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { AlertContextProvider } from '../../contexts/alert-context';
import { registerStoreDisabledHandler } from '../../helpers/api-errors';
import { revalidateStoreAccessNow } from '../../query/store-access';
import AlertsView from '../alerts-view';
import { LastImport } from '../dashboard/last-import';
import { AppNavBar } from '../navs/app-nav-bar';
import { RefreshingIndicator } from '../refreshing-indicator';
import { Updater } from '../updater';
import { useCurrentStore } from './use-current-store';

// App shell rendered by the root route.
export function RootLayout() {
  const store = useCurrentStore();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (store) {
      document.title = `${store.name} | Orderbot`;
      datadogRum.setUserProperty('store_product_key', store.product_key);
    } else {
      document.title = 'Orderbot';
    }
  }, [store]);

  // Skip the app shell for /signup — only show the redirect content
  if (pathname === '/signup') {
    return <Outlet />;
  }

  return (
    <AlertContextProvider>
      <StoreDisabledListener />
      <RefreshingIndicator />
      <div className="h-full grid grid-rows-[auto_1fr_auto]">
        <AppNavBar />
        <div className="h-full overflow-hidden">
          <div className="h-full overflow-y-auto flex flex-col">
            <Outlet />
          </div>
        </div>
        <Footer>{store && <LastImport store={store} />}</Footer>
        <Updater />
      </div>
      <AlertsView />
    </AlertContextProvider>
  );
}

// Bridges the QueryClient's store-disabled reports (raised outside React) into a
// router refresh: invalidating the stores query first means storeLayout.beforeLoad
// re-runs against fresh data and lands on the takeover. This covers the
// mid-session race — the stores query is IndexedDB-persisted with a 3-day max
// age, so a store disabled mid-session reads enabled:true until the next refetch.
export function StoreDisabledListener() {
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    registerStoreDisabledHandler(() => revalidateStoreAccessNow(queryClient, router));

    return () => registerStoreDisabledHandler(null);
  }, [queryClient, router]);

  return null;
}
